/**
 * Axiom Analytics Service
 * Provides structured logging and analytics to Axiom.co
 * Automatically includes device fingerprint and context with all events
 */

import { Axiom } from "@axiomhq/js";
import { configService } from "@/services/config.service";
import { useFingerprint } from "@/composables/useFingerprint";

export interface AxiomConfig {
  apiToken?: string;
  dataset?: string;
  environment?: string;
  enabled?: boolean;
}

export interface AxiomEvent {
  event: string;
  level?: "debug" | "info" | "warn" | "error";
  category?: string;
  properties?: Record<string, any>;
  user?: {
    id?: string;
    clerk?: string;
    domain?: string;
    workgroup?: string;
  };
  timestamp?: string;
}

class AxiomService {
  private client: Axiom | null = null;
  private config: AxiomConfig = {};
  private deviceId: string | null = null;
  private deviceInfo: Record<string, any> = {};
  private sessionId: string;
  private eventBuffer: AxiomEvent[] = [];
  private flushTimer: number | null = null;
  private readonly FLUSH_INTERVAL = 5000; // 5 seconds
  private readonly MAX_BUFFER_SIZE = 50;

  constructor() {
    // Generate session ID
    this.sessionId = this.generateSessionId();
  }

  /**
   * Initialize Axiom service
   */
  async initialize(config?: AxiomConfig): Promise<void> {
    // Hardcoded Axiom configuration
    const hardcodedConfig: AxiomConfig = {
      apiToken: "xaat-3eb3170f-c0e0-4573-be38-789b718a67ba",
      dataset: "scantixpro",
      environment: window.location.hostname || "localhost",
      enabled: true,
    };

    // Use hardcoded config, but allow override from parameter if provided
    this.config = { ...hardcodedConfig, ...config };

    // Always use hardcoded token and dataset for security
    this.config.apiToken = hardcodedConfig.apiToken;
    this.config.dataset = hardcodedConfig.dataset;

    // Use domain as environment
    this.config.environment = window.location.hostname || "localhost";

    if (!this.config.enabled) {
      console.log("Axiom analytics is disabled");
      return;
    }

    try {
      // Initialize Axiom client
      this.client = new Axiom({
        token: this.config.apiToken,
        orgId: this.config.dataset.split("-")[0], // Extract org from dataset
      });

      // Get device fingerprint
      const fingerprint = useFingerprint();
      await fingerprint.initFingerprint();
      this.deviceId = fingerprint.visitorId.value;
      this.deviceInfo = fingerprint.getDeviceInfo();

      // Start flush timer
      this.startFlushTimer();

      console.log("✅ Axiom analytics initialized", {
        dataset: this.config.dataset,
        environment: this.config.environment,
        deviceId: this.deviceId,
        sessionId: this.sessionId,
      });

      // Log initialization event
      this.logEvent({
        event: "app_initialized",
        category: "system",
        level: "info",
        properties: {
          version: import.meta.env.VITE_APP_VERSION || "1.0.0",
          environment: this.config.environment,
          domain: window.location.hostname,
        },
      });
    } catch (error) {
      console.error("Failed to initialize Axiom:", error);
    }
  }

  /**
   * Get configuration from window.SCANTIX_CONFIG
   */
  private getConfigFromWindow(): AxiomConfig {
    const windowConfig = (window as any).SCANTIX_CONFIG || {};

    return {
      apiToken: windowConfig.AXIOM_API_TOKEN,
      dataset: windowConfig.AXIOM_DATASET || "scantix-logs",
      environment:
        windowConfig.AXIOM_ENVIRONMENT ||
        windowConfig.SENTRY_ENVIRONMENT ||
        "production",
      enabled: windowConfig.AXIOM_ENABLED !== false, // Default to enabled
    };
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Extract module name from API endpoint URL
   */
  private extractModuleFromUrl(url: string): string {
    // Check for full URLs
    if (url.includes("/v0/auth/")) return "auth";
    if (url.includes("/scantix/v0/scanner/")) return "scanner";
    if (url.includes("/scantix/v0/reports/")) return "reports";
    if (url.includes("/config/v0/")) return "config";
    if (url.includes("/tickets/")) return "tickets";

    // Check for frontend routes
    if (url.includes("/login")) return "auth";
    if (url.includes("/scanner")) return "scanner";
    if (url.includes("/reports")) return "reports";
    if (url.includes("/settings")) return "settings";
    if (url.includes("/results")) return "scanner";

    // Default to general if no match
    return "general";
  }

  /**
   * Determine module from current context
   */
  private getCurrentModule(): string {
    // Check current route
    const path = window.location.pathname;
    return this.extractModuleFromUrl(path);
  }

  /**
   * Start the flush timer
   */
  private startFlushTimer(): void {
    if (this.flushTimer) return;

    this.flushTimer = window.setInterval(() => {
      this.flush();
    }, this.FLUSH_INTERVAL);
  }

  /**
   * Stop the flush timer
   */
  private stopFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /**
   * Log an event to Axiom
   */
  logEvent(event: AxiomEvent): void {
    if (!this.config.enabled) return;

    // Determine module from event properties or current context
    const module =
      event.properties?.module || event.properties?.endpoint
        ? this.extractModuleFromUrl(event.properties.endpoint)
        : this.getCurrentModule();

    // Enrich event with context
    const enrichedEvent = {
      ...event,
      timestamp: event.timestamp || new Date().toISOString(),
      _time: new Date().toISOString(),

      // Module context
      module: module,

      // Device context
      deviceId: this.deviceId,
      sessionId: this.sessionId,
      ...this.deviceInfo,

      // App context
      app: {
        name: "ScantixPro",
        version: import.meta.env.VITE_APP_VERSION || "1.0.0",
        environment: this.config.environment,
        domain: window.location.hostname,
        apiUrl: configService.getApiBaseUrl(),
      },

      // User context (if provided)
      user: event.user,

      // Event metadata
      level: event.level || "info",
      category: event.category || "general",

      // Custom properties
      ...event.properties,
    };


    // Add to buffer
    this.eventBuffer.push(enrichedEvent);

    // Flush if buffer is full
    if (this.eventBuffer.length >= this.MAX_BUFFER_SIZE) {
      this.flush();
    }
  }

  /**
   * Log a debug event
   */
  debug(message: string, properties?: Record<string, any>): void {
    this.logEvent({
      event: message,
      level: "debug",
      properties,
    });
  }

  /**
   * Log an info event
   */
  info(message: string, properties?: Record<string, any>): void {
    this.logEvent({
      event: message,
      level: "info",
      properties,
    });
  }

  /**
   * Log a warning event
   */
  warn(message: string, properties?: Record<string, any>): void {
    this.logEvent({
      event: message,
      level: "warn",
      properties,
    });
  }

  /**
   * Log an error event
   */
  error(
    message: string,
    error?: Error | unknown,
    properties?: Record<string, any>,
  ): void {
    const errorDetails: Record<string, any> = {};

    if (error instanceof Error) {
      errorDetails.error = {
        message: error.message,
        stack: error.stack,
        name: error.name,
      };
    } else if (error) {
      errorDetails.error = error;
    }

    this.logEvent({
      event: message,
      level: "error",
      properties: {
        ...errorDetails,
        ...properties,
      },
    });
  }

  /**
   * Track a user action
   */
  track(action: string, properties?: Record<string, any>): void {
    this.logEvent({
      event: action,
      category: "user_action",
      properties,
    });
  }

  /**
   * Track page view
   */
  trackPageView(page: string, properties?: Record<string, any>): void {
    const module = this.extractModuleFromUrl(page);
    this.logEvent({
      event: "page_view",
      category: "navigation",
      properties: {
        page,
        module,
        url: window.location.href,
        referrer: document.referrer,
        ...properties,
      },
    });
  }

  /**
   * Track API call
   */
  trackApiCall(
    endpoint: string,
    method: string,
    status: number,
    duration: number,
    properties?: Record<string, any>,
  ): void {
    const module = this.extractModuleFromUrl(endpoint);


    this.logEvent({
      event: "api_call",
      category: "api",
      properties: {
        endpoint,
        module,
        method,
        status,
        duration,
        success: status >= 200 && status < 300,
        ...properties,
      },
    });
  }

  /**
   * Track barcode scan
   */
  trackScan(
    format: string,
    value: string,
    success: boolean,
    properties?: Record<string, any>,
  ): void {
    this.logEvent({
      event: "barcode_scan",
      category: "scanner",
      properties: {
        module: "scanner",
        format,
        value: value.substring(0, 50), // Truncate for privacy
        success,
        scanMethod: properties?.scanMethod || "camera",
        ...properties,
      },
    });
  }

  /**
   * Set user context
   */
  setUser(user: {
    id?: string;
    clerk?: string;
    domain?: string;
    workgroup?: string;
  }): void {
    // Store user for future events
    this.currentUser = user;

    // Log user identification event
    this.logEvent({
      event: "user_identified",
      category: "auth",
      user,
      properties: {
        timestamp: new Date().toISOString(),
      },
    });
  }

  private currentUser?: {
    id?: string;
    clerk?: string;
    domain?: string;
    workgroup?: string;
  };

  /**
   * Clear user context
   */
  clearUser(): void {
    this.currentUser = undefined;

    this.logEvent({
      event: "user_cleared",
      category: "auth",
    });
  }

  /**
   * Flush events to Axiom
   */
  async flush(): Promise<void> {
    if (!this.client || this.eventBuffer.length === 0) return;

    const events = [...this.eventBuffer];
    this.eventBuffer = [];

    try {
      // Add current user to all events if set
      const eventsWithUser = events.map((event) => ({
        ...event,
        user: event.user || this.currentUser,
      }));

      // Send to Axiom
      await this.client.ingest(this.config.dataset!, eventsWithUser);

      console.debug(`Flushed ${events.length} events to Axiom`);
    } catch (error) {
      console.error("Failed to flush events to Axiom:", error);

      // Re-add events to buffer for retry
      this.eventBuffer.unshift(...events);

      // Trim buffer if too large
      if (this.eventBuffer.length > this.MAX_BUFFER_SIZE * 2) {
        this.eventBuffer = this.eventBuffer.slice(0, this.MAX_BUFFER_SIZE);
      }
    }
  }

  /**
   * Shutdown the service
   */
  async shutdown(): Promise<void> {
    this.stopFlushTimer();
    await this.flush();

    if (this.client) {
      await this.client.flush();
    }
  }
}

// Export singleton instance
export const axiomService = new AxiomService();

// Export for use in Vue components
export function useAxiom() {
  return axiomService;
}
